import { Landmark, Mountain, Music, Truck, ArrowUpRight } from "lucide-react";
import { Reveal, Btn, Overline, MaskedLine } from "@/components/motion-bits";
import riverImg from "@/assets/about_memphis_river.jpg";

const BADGE = [
  {
    icon: Landmark,
    title: "The Bridge",
    copy: "The Memphis Bridge — the crossing we're named after, and the viaduct every run starts across.",
  },
  {
    icon: Mountain,
    title: "The Pyramid",
    copy: "The Memphis Pyramid — the signature on our skyline and the landmark that says we're home.",
  },
  {
    icon: Music,
    title: "The Blues",
    copy: "Music notes and a saxophone — Memphis blues, the soundtrack of the city we haul for.",
  },
  {
    icon: Truck,
    title: "The Truck",
    copy: "The work itself — regional and over-the-road freight, moved with reliance.",
  },
];

const About = () => (
  <div data-testid="about-page">
    {/* Hero */}
    <section className="px-6 lg:px-10 py-16 md:py-24">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <Overline className="text-brand-red">
            <MaskedLine delay={0.1}>About Memphis Viaduct Inc</MaskedLine>
          </Overline>
          <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.9] text-[clamp(3rem,8vw,7rem)]">
            <MaskedLine delay={0.24}>Built at</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-brand-teal">the crossing.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink/75">
              Memphis Viaduct Inc was established in 2025 on the bank of the Mississippi River —
              the spot where I-55 and I-40 meet and where freight heading any direction funnels
              across the water. We're a trucking company built on one word: reliance. Say when it
              moves, and it moves.
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="mt-8 flex flex-wrap gap-4">
              <Btn to="/services" variant="red" testid="about-services-cta">
                See the Lanes <ArrowUpRight size={20} aria-hidden="true" />
              </Btn>
              <Btn href="tel:+18338123828" variant="cream" testid="about-phone-cta">833-V1aduct</Btn>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-5">
          <Reveal delay={0.2}>
            <div className="rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg">
              <img
                src={riverImg}
                alt="Illustration of the Memphis riverfront with the bridge, pyramid and a blues saxophone player"
                className="w-full aspect-square object-cover"
                data-testid="about-hero-illustration"
              />
              <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-teal">
                The river bank · Memphis, TN
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Reading the badge */}
    <section className="bg-paper border-y-[3px] border-ink px-6 lg:px-10 py-20 md:py-28">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <Overline className="text-brand-red">Reading the Badge</Overline>
          <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl">
            Every mark means Memphis.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {BADGE.map((b, i) => (
            <Reveal key={b.title} delay={0.08 * i}>
              <div
                className="h-full border-[3px] border-ink bg-cream p-6 shadow-block transition-transform duration-200 hover:-translate-y-1"
                data-testid={`about-badge-${b.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <span className="inline-flex h-12 w-12 items-center justify-center border-[3px] border-ink bg-brand-gold text-ink">
                  <b.icon size={24} aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display uppercase tracking-tight text-2xl">{b.title}</h3>
                <p className="mt-3 leading-relaxed text-ink/70">{b.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* The promise */}
    <section className="px-6 lg:px-10 py-20 md:py-28">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <Reveal>
            <Overline className="text-brand-red">The Promise</Overline>
            <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
              "Your Road to Reliance" isn't a slogan we printed.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 text-lg leading-relaxed text-ink/75">
              It's the way we run freight. Our lanes come out of the I-55 &amp; I-40 crossing
              point on the Mississippi River bank, and our reputation rides on two things: being
              where we said we'd be, and being honest about home time. Shippers get loads that
              arrive when promised. Drivers get schedules they can plan a life around.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-lg leading-relaxed text-ink/75">
              That's the whole business. The bridge, the pyramid, the blues and the saxophone on
              our badge are just Memphis telling you where we're from — reliance is what we bring
              to your road.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <Reveal delay={0.15}>
            <div className="-rotate-1 border-[3px] border-ink bg-brand-red p-6 md:p-8 text-cream shadow-blocklg">
              <p className="font-display uppercase tracking-tight text-3xl md:text-4xl leading-tight">
                "It isn't a tagline — it's the promise we run."
              </p>
              <p className="mt-4 text-sm font-bold uppercase tracking-[0.25em] text-cream/80">
                Memphis Viaduct Inc · Est. 2025
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
);

export default About;
